import { PAGINATION_PAGE_PARAM, SearchParamController } from '@clockout-dev/design-elements';
import { calculateDateRange, DASHBOARD_REPORT_GROUPING } from '../../utils/dates';
import { isCanceledError } from '../../utils/httpErrors';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import { DASHBOARD_TABS, USER_SUMMARY_PAGE_SIZE } from './dashboard.constants';
import DashboardDelegate from './dashboard.delegate';

const TAB_SEARCH_PARAM = 'tab';

class DashboardController extends Inherit(ClockoutController, DashboardDelegate, SearchParamController) {
    static buildToggleBarChartData(data = {}, toggleKey, series = [], bucketKey = 'period') {
        const rows = Array.isArray(data[toggleKey]) ? data[toggleKey] : [];
        return rows.flatMap((row) =>
            series.map((serie) => ({
                bucket: row[bucketKey],
                label: serie.label,
                value: row[serie.key] ?? 0,
            }))
        );
    }

    /** Dashboard tabs the current session actually holds the scope for. */
    getAccessibleDashboardTabs() {
        return DASHBOARD_TABS.filter((tab) => this.hasScope(tab.requiredScope));
    }

    /**
     * Reads the active tab from `?tab=`. Call from `initialize()` (post-mount),
     * not from a state field initializer — `getSearchParamValue` is SSR-safe
     * (returns `null` without `window`) but resolving here avoids a server/client
     * markup mismatch when `?tab=` differs from `DEFAULT_TAB_KEY`.
     *
     * Only ever lands on a tab the session can access — a deep link or stale
     * `?tab=` pointing at a scope the role doesn't hold falls through to the
     * first accessible tab instead.
     *
     * Returns `null` when the session holds no tab scope at all. Defaulting
     * to a tab key here would hand `fetchData()` a tab to fetch and
     * `<DashboardTabs>` a tab to render, both behind an empty tab strip —
     * the screen scope alone must not surface any tab's contents.
     */
    resolveInitialTab() {
        const fromUrl = this.getSearchParamValue(TAB_SEARCH_PARAM);
        const accessibleTabs = this.getAccessibleDashboardTabs();
        if (accessibleTabs.some((tab) => tab.key === fromUrl)) return fromUrl;
        return accessibleTabs[0]?.key ?? null;
    }

    handleTabChange(key) {
        if (this.state.activeTab === key) return;
        if (!this.getAccessibleDashboardTabs().some((tab) => tab.key === key)) return;

        // Cancel any in-flight payload fetch for the tab we're leaving so its
        // stale result can't land on the new tab (the cached-tab path below does
        // not go through fetchData, which is where the abort otherwise happens).
        if (this._dashboardAbort) this._dashboardAbort.abort();

        this.setSearchParamValue(TAB_SEARCH_PARAM, key);
        // User Summary table uses `saveSearchParams` (`page` / `perPage`). Clear
        // stale pagination when switching tabs so Enrollment and Usage do not
        // share a page index (same pattern as Reports).
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.resetUserSummary();
        this.resetTransactionSummary();

        this.setState({ activeTab: key }, () => {
            const cached = this.state.tabCache?.[key];
            if (cached) {
                this.setState({ dashboardData: cached }, async () => {
                    if (key === '1') await this.ensureUserAdoptionMetricsLoaded();
                    if (key === '1') this.loadFunnel();
                    if (key === '2') await this.ensureEngagementSummaryMetricsLoaded();
                    if (key === '1' || key === '2') {
                        const { timeFrame, dateRange } = this.state;
                        const { start, end } = calculateDateRange(timeFrame, dateRange);
                        if (start && end) {
                            if (key === '2') {
                                await this.loadTransactionSummaryPage(1, USER_SUMMARY_PAGE_SIZE, { start, end });
                            } else {
                                await this.loadUserSummaryPage(1, USER_SUMMARY_PAGE_SIZE, { start, end });
                            }
                        }
                    }
                });
            } else {
                this.fetchData();
            }
        });
    }

    async initialize() {
        try {
            if (this.user?.access === false) {
                this.logout();
                return;
            }
            // Check if user has valid data (not just an empty object)
            if (!this.user || !this.user.access || this.user.access.length === 0) {
                this.navigateLogin();
                return;
            }

            const activeTab = this.resolveInitialTab();
            const ytdReport = this.data?.user ? await this.data.fetchYTDReport(this.data?.user?.access[0]) : {};
            const user = this.data.user;
            const segmentPolicy = user?.fi?.segment_policy;
            const hasSegmentPolicy = segmentPolicy && Object.keys(segmentPolicy).length > 0;
            await new Promise((resolve) => {
                this.setState(
                    { user, ytdReport, activeTab, showConfigurationReminder: !hasSegmentPolicy },
                    resolve,
                );
            });
            await this.fetchData();
        } catch (error) {
            console.log('Error in initialize', error);
        }
    }

    /**
     * Fetch the active tab's analytics payload from the real dashboard
     * endpoint. No static fixtures / defaults — when the API returns empty
     * or errors, `dashboardData` is null and each tab renders its empty
     * state with real (zero/empty) values.
     */
    async fetchData() {
        const { timeFrame, dateRange, activeTab } = this.state;
        // No accessible tab — nothing to fetch. Without this the tab switch
        // below falls through to its `else` and pulls Enrollment for a
        // session that holds no tab scope at all.
        if (!activeTab) {
            this.setState({ loading: false });
            return;
        }
        if (timeFrame === 'custom' && (!dateRange?.[0] || !dateRange?.[1])) {
            this.setState({ loading: false });
            return;
        }

        // Supersede any in-flight payload fetch: aborting the previous request
        // cancels its HTTP call so a tab/date change queries immediately with the
        // new info instead of waiting for the stale one. `ac.signal.aborted`
        // doubles as the stale-result guard for this cycle.
        if (this._dashboardAbort) this._dashboardAbort.abort();
        const ac = new AbortController();
        this._dashboardAbort = ac;

        this.setState({ loading: true });
        try {
            const { start, end } = calculateDateRange(timeFrame, dateRange);
            const options = {
                start,
                end,
                grouping: DASHBOARD_REPORT_GROUPING,
                timeFrame,
                signal: ac.signal,
            };

            let dashboardData = null;
            if (this.data?.aba) {
                if (activeTab === '2') {
                    dashboardData = await this.data.fetchDashboardUsage(options);
                } else if (activeTab === '3') {
                    dashboardData = await this.data.fetchDashboardMonetization(options);
                } else {
                    dashboardData = await this.data.fetchDashboardEnrollment(options);
                }
            }

            // A newer fetch superseded this one while it was in flight — drop the
            // stale result rather than flashing it into the UI.
            if (ac.signal.aborted) return;

            this.setState((prev) => ({
                dashboardData,
                loading: false,
                tabCache: dashboardData
                    ? { ...prev.tabCache, [activeTab]: dashboardData }
                    : prev.tabCache,
            }));

            if (dashboardData) {
                const postFetchTasks = [];
                if (activeTab === '1') {
                    postFetchTasks.push(this.refreshUserAdoptionMetricsForPeriod({ start, end }));
                }
                if (activeTab === '2') {
                    postFetchTasks.push(this.refreshEngagementSummaryMetricsForPeriod({ start, end }));
                }
                if (activeTab === '1') {
                    postFetchTasks.push(this.refreshUserSummaryForPeriod({ start, end }));
                    postFetchTasks.push(this.loadFunnel());
                }
                if (activeTab === '2') {
                    postFetchTasks.push(this.refreshTransactionSummaryForPeriod({ start, end }));
                }
                if (postFetchTasks.length) {
                    await Promise.all(postFetchTasks);
                }
            }
        } catch (error) {
            // Aborted/superseded fetch — leave the newer cycle's state alone.
            if (isCanceledError(error) || ac.signal.aborted) return;
            console.error('Error fetching data:', error);
            this.setState({ dashboardData: null, loading: false });
        }
    }

    /** Clear the per-tab data cache whenever the date range changes. */
    clearTabCache() {
        this.resetUserAdoptionMetrics();
        this.resetEngagementSummaryMetrics();
        this.resetUserSummary();
        this.resetTransactionSummary();
        this.setState({ tabCache: {} });
    }

    async handleTimeFrameChange(timeFrame) {
        const nextDateRange = timeFrame === 'custom' ? this.state.dateRange : null;

        this.resetUserAdoptionMetrics();
        this.resetEngagementSummaryMetrics();
        this.resetUserSummary();
        this.resetTransactionSummary();
        this.setState(
            {
                timeFrame,
                dateRange: nextDateRange,
                tabCache: {},
            },
            async () => {
                if (timeFrame !== 'custom') await this.fetchData();
                else if (nextDateRange?.[0] && nextDateRange?.[1]) await this.fetchData();
            }
        );
    }

    async handleDateRangeChange(dates) {
        if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
            this.setState({ dateRange: null });
            return;
        }

        this.resetUserAdoptionMetrics();
        this.resetEngagementSummaryMetrics();
        this.resetUserSummary();
        this.resetTransactionSummary();
        this.setState(
            {
                timeFrame: 'custom',
                dateRange: dates,
                tabCache: {},
            },
            async () => {
                await this.fetchData();
            }
        );
    }

    handleConfigurationReminderDismiss() {
        this.setState({ showConfigurationReminder: false });
    }
}

export default DashboardController;
